import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

const AyurvedaCard = React.forwardRef(({ 
  className, 
  children, 
  variant = 'default',
  animate = true,
  hover = true,
  breathe = false,
  ...props 
}, ref) => {
  const variants = {
    default: 'glass-card',
    subtle: 'glass-subtle',
    solid: 'bg-white/90 backdrop-blur-sm border border-ayurveda-sage-200 shadow-lg',
  };

  const motionProps = animate ? {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
  } : {};

  const hoverProps = hover ? {
    whileHover: { 
      scale: 1.02,
      boxShadow: '0 20px 50px 0 rgba(93, 122, 93, 0.2)',
      transition: { duration: 0.3 }
    }
  } : {};

  const CardComponent = animate ? motion.div : 'div';

  return (
    <CardComponent
      ref={ref}
      className={cn(
        variants[variant],
        'rounded-2xl p-6 transition-all duration-300',
        breathe && 'breathe',
        className
      )}
      {...motionProps}
      {...hoverProps}
      {...props}
    >
      {children}
    </CardComponent>
  );
});

AyurvedaCard.displayName = 'AyurvedaCard';

const AyurvedaCardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 pb-4', className)}
    {...props}
  />
));
AyurvedaCardHeader.displayName = 'AyurvedaCardHeader';

const AyurvedaCardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'ayurveda-heading text-2xl font-semibold leading-none tracking-tight text-ayurveda-sage-700',
      className
    )}
    {...props}
  />
));
AyurvedaCardTitle.displayName = 'AyurvedaCardTitle';

const AyurvedaCardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-ayurveda-sage-600 leading-relaxed', className)}
    {...props}
  />
));
AyurvedaCardDescription.displayName = 'AyurvedaCardDescription';

const AyurvedaCardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('pt-0', className)} {...props} />
));
AyurvedaCardContent.displayName = 'AyurvedaCardContent';

const AyurvedaCardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center pt-4', className)}
    {...props}
  />
));
AyurvedaCardFooter.displayName = 'AyurvedaCardFooter';

export {
  AyurvedaCard,
  AyurvedaCardHeader,
  AyurvedaCardTitle,
  AyurvedaCardDescription,
  AyurvedaCardContent,
  AyurvedaCardFooter,
};